import type {
  ChannelMenuItem,
  LocalizedText,
  NewOrderLine,
  Satang,
} from '../api/types';

/**
 * The cart, as pure data and pure functions. No React, so it can be tested and
 * reasoned about on its own.
 *
 * ## Why a line snapshots its price and name
 *
 * The server snapshots both when a line is added to an order, for a reason the
 * contract states outright: a menu rename must not rewrite yesterday's receipt,
 * and a later price change must not re-price an open ticket. The cart does the
 * same thing one step earlier, which is what makes "the price of X changed"
 * detectable at all — without a snapshot there is nothing to compare against
 * and the guest silently pays a different number than the one they chose.
 *
 * ## What the cart does NOT compute
 *
 * A total. `estimateSubtotalSatang` is an estimate and is named like one. VAT
 * mode, service charge, rounding policy and the tax shape stored *on the order*
 * (D-020) all live on the server, and a client that recomputes from anything
 * else will disagree with the bill — while still balancing internally, so
 * nothing catches it. The authoritative numbers are `Order.totals`, and they
 * exist only once the order does.
 */

export interface CartModifier {
  modifier_id: string;
  /** For display in the cart without re-reading the menu. */
  name_snapshot: LocalizedText;
  price_delta_satang: Satang;
  quantity: number;
}

export interface CartLine {
  /** Client-minted UUIDv7. Becomes `NewOrderLine.line_id` — a retry cannot duplicate it. */
  line_id: string;
  item_id: string;
  name_snapshot: LocalizedText;
  /** The effective channel price when this line went in. See above. */
  unit_price_satang: Satang;
  quantity: number;
  modifiers: CartModifier[];
  /** Free-text note to the kitchen. Whether guests may write one at all is OPEN (03 §Q4). */
  note?: string | null;
}

export interface CartState {
  lines: CartLine[];
}

export const EMPTY_CART: CartState = {lines: []};

/** Unit price plus the signed modifier deltas. Never includes tax. */
export function lineUnitSatang(line: CartLine): Satang {
  const modifiers = line.modifiers.reduce(
    (sum, modifier) => sum + modifier.price_delta_satang * modifier.quantity,
    0,
  );
  return line.unit_price_satang + modifiers;
}

/** What one cart line comes to, before anything the server adds. */
export function lineTotalSatang(line: CartLine): Satang {
  return lineUnitSatang(line) * line.quantity;
}

/**
 * An **estimate**, from menu prices only. Not the bill.
 *
 * No VAT, no service charge, no rounding, no discount — the server applies all
 * four from the tax shape it stores on the order. Show it under
 * `q5.estimatedTotal` with `q5.estimateNote`, in `MoneyTone.neutral`, and never
 * as a total the guest owes.
 */
export function estimateSubtotalSatang(state: CartState): Satang {
  return state.lines.reduce((sum, line) => sum + lineTotalSatang(line), 0);
}

export function itemCount(state: CartState): number {
  return state.lines.reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * Two lines are the same *choice* when they are the same item with the same
 * modifiers and the same note. Adding that choice again bumps the quantity
 * rather than stacking a second identical row, which is what a guest expects
 * and what keeps a KDS ticket readable.
 *
 * Modifier order does not matter, so it is sorted before comparison.
 */
export function isSameChoice(a: CartLine, b: CartLine): boolean {
  if (a.item_id !== b.item_id) return false;
  if ((a.note ?? '') !== (b.note ?? '')) return false;
  if (a.modifiers.length !== b.modifiers.length) return false;
  const key = (line: CartLine) =>
    line.modifiers
      .map((m) => `${m.modifier_id}:${m.quantity}`)
      .sort()
      .join('|');
  return key(a) === key(b);
}

export function addLine(state: CartState, line: CartLine): CartState {
  const existing = state.lines.findIndex((candidate) => isSameChoice(candidate, line));
  if (existing === -1) return {lines: [...state.lines, line]};
  return {
    lines: state.lines.map((candidate, index) =>
      index === existing
        ? {...candidate, quantity: candidate.quantity + line.quantity}
        : candidate,
    ),
  };
}

export function removeLine(state: CartState, lineId: string): CartState {
  return {lines: state.lines.filter((line) => line.line_id !== lineId)};
}

/** Quantity at or below zero removes the line — a stepper taken to 0 means "gone". */
export function setQuantity(state: CartState, lineId: string, quantity: number): CartState {
  if (quantity <= 0) return removeLine(state, lineId);
  return {
    lines: state.lines.map((line) =>
      line.line_id === lineId ? {...line, quantity} : line,
    ),
  };
}

export interface CartLineIssue {
  line: CartLine;
  /**
   * `sold_out`  — 86'd, deactivated or off-schedule since it went in the cart.
   *               **Never drop it silently** (03 §Q5): the guest chose it and
   *               has to be told, with a way forward.
   * `gone`      — the item is not in the projection at all any more.
   * `reprice`   — the effective channel price moved since it was added.
   */
  kind: 'sold_out' | 'gone' | 'reprice';
  /** For `reprice`: the price now. */
  currentUnitPriceSatang?: Satang;
}

/**
 * Everything wrong with the cart right now, given the live menu.
 *
 * Pure and re-derived on every render rather than stored, so an 86 that arrives
 * on the stream shows up without anything having to remember to recheck.
 */
export function findCartIssues(
  state: CartState,
  lookup: {
    item: (itemId: string) => ChannelMenuItem | undefined;
    isAvailable: (itemId: string) => boolean;
  },
): CartLineIssue[] {
  const issues: CartLineIssue[] = [];
  for (const line of state.lines) {
    const item = lookup.item(line.item_id);
    if (!item) {
      issues.push({line, kind: 'gone'});
      continue;
    }
    if (!lookup.isAvailable(line.item_id)) {
      issues.push({line, kind: 'sold_out'});
      continue;
    }
    if (item.price_satang !== line.unit_price_satang) {
      issues.push({
        line,
        kind: 'reprice',
        currentUnitPriceSatang: item.price_satang,
      });
    }
  }
  return issues;
}

/** Accept the current price for a line, clearing a `reprice` issue. */
export function acceptPrice(
  state: CartState,
  lineId: string,
  unitPriceSatang: Satang,
): CartState {
  return {
    lines: state.lines.map((line) =>
      line.line_id === lineId ? {...line, unit_price_satang: unitPriceSatang} : line,
    ),
  };
}

/**
 * The cart as `SubmitOrderRequest.lines`.
 *
 * Deliberately carries no money: the contract says money fields in the request
 * are ignored, because a device does not get to decide what a bill costs. Only
 * ids, quantities and the note cross the wire.
 */
export function toNewOrderLines(state: CartState): NewOrderLine[] {
  return state.lines.map((line) => ({
    line_id: line.line_id,
    item_id: line.item_id,
    quantity: line.quantity,
    modifiers: line.modifiers.map((modifier) => ({
      modifier_id: modifier.modifier_id,
      quantity: modifier.quantity,
    })),
    note: line.note ?? null,
  }));
}
